// Generat automat din README.md
import React, { useState, useEffect } from 'react';
import { FileText, BarChart3, Download, Calendar, Users, DollarSign, TrendingUp, Send, Clock, Filter, Eye, Zap, Mail, PieChart, Activity } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';

const RaportareAutomata = () => {
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [filterPeriod, setFilterPeriod] = useState('month');

  // Mock data pentru raportare automată
  const reportData = {
    overview: {
      totalReports: 156,
      scheduledReports: 12,
      automatedReports: 24,
      savedTime: '450 ore'
    },
    insights: {
      topSellingProduct: 'Laptop Dell Inspiron',
      bestPerformingTeam: 'Echipa București',
      revenueGrowth: '+23.5%',
      averageOrderValue: '2,450 RON'
    },
    scheduledReports: [
      {
        id: 1,
        name: 'Raport Vânzări Săptămânal',
        type: 'Vânzări',
        frequency: 'Săptămânal',
        nextRun: '2024-03-18 09:00',
        recipients: ['manager@company.ro', 'sales@company.ro'],
        status: 'Activ',
        lastSent: '2024-03-11 09:00',
        format: 'PDF + Excel',
        insights: true,
        automation: 'Complet',
        description: 'Analiza completă a vânzărilor pe echipe și produse cu recomandări AI'
      },
      {
        id: 2,
        name: 'Dashboard Performanță Echipe',
        type: 'Performanță',
        frequency: 'Zilnic',
        nextRun: '2024-03-16 08:30',
        recipients: ['ceo@company.ro', 'hr@company.ro'],
        status: 'Activ',
        lastSent: '2024-03-15 08:30',
        format: 'PDF',
        insights: true,
        automation: 'Semi-automat',
        description: 'KPI-uri echipe, obiective și predicții de performanță'
      },
      {
        id: 3,
        name: 'Analiza Clienți și Segmentare',
        type: 'CRM',
        frequency: 'Lunar',
        nextRun: '2024-04-01 10:00',
        recipients: ['marketing@company.ro', 'crm@company.ro'],
        status: 'Activ',
        lastSent: '2024-03-01 10:00',
        format: 'Excel + Grafice',
        insights: true,
        automation: 'Complet',
        description: 'Segmentare avansată clienți cu oportunități de cross-sell/upsell'
      },
      {
        id: 4,
        name: 'Inventar și Predicții Stoc',
        type: 'Inventar',
        frequency: 'Bi-săptămânal',
        nextRun: '2024-03-20 07:00',
        recipients: ['warehouse@company.ro', 'procurement@company.ro'],
        status: 'Pauză',
        lastSent: '2024-03-06 07:00',
        format: 'PDF',
        insights: false,
        automation: 'Manual',
        description: 'Status stocuri, alerte low-stock și recomandări de reaprovizionare'
      },
      {
        id: 5,
        name: 'Raport Financiar Executiv',
        type: 'Financiar',
        frequency: 'Lunar',
        nextRun: '2024-04-05 11:00',
        recipients: ['cfo@company.ro', 'board@company.ro'],
        status: 'Activ',
        lastSent: '2024-03-05 11:00',
        format: 'PDF Premium',
        insights: true,
        automation: 'Complet',
        description: 'Overview financiar complet cu predicții și recomandări strategice'
      }
    ],
    recentReports: [
      {
        id: 101,
        name: 'Vânzări T1 2024 - Finalizat',
        generatedAt: '2024-03-15 14:30',
        type: 'Vânzări',
        size: '2.4 MB',
        views: 23,
        downloads: 8,
        recipients: 5,
        highlights: ['Creștere 18% revenue', '156 clienți noi', 'Top produs: Laptopuri']
      },
      {
        id: 102,
        name: 'Performanță Echipe - Martie',
        generatedAt: '2024-03-14 09:15',
        type: 'HR',
        size: '1.8 MB',
        views: 45,
        downloads: 12,
        recipients: 8,
        highlights: ['Echipa B: +25% performanță', '92% satisfacție client', '3 obiective atinse']
      },
      {
        id: 103,
        name: 'Analiza Competiție Q1',
        generatedAt: '2024-03-13 16:20',
        type: 'Market Intelligence',
        size: '3.1 MB',
        views: 67,
        downloads: 15,
        recipients: 6,
        highlights: ['Market share +5%', '2 noi competitori', 'Pricing optimal']
      },
      {
        id: 104,
        name: 'Customer Journey Analytics',
        generatedAt: '2024-03-12 11:45',
        type: 'CRM',
        size: '1.5 MB',
        views: 34,
        downloads: 9,
        recipients: 7,
        highlights: ['Conversion rate +12%', 'Touch points optimizate', '67% retention']
      }
    ],
    templates: [
      {
        id: 1,
        name: 'Sales Dashboard Standard',
        category: 'Vânzări',
        description: 'Template standard pentru rapoarte de vânzări cu KPI-uri principale',
        usage: 45,
        lastUpdated: '2024-03-10',
        elements: ['Revenue charts', 'Team performance', 'Product analysis', 'Forecasting']
      },
      {
        id: 2,
        name: 'Executive Summary',
        category: 'Management',
        description: 'Format executiv cu highlights și recomandări strategice',
        usage: 28,
        lastUpdated: '2024-03-05',
        elements: ['Key metrics', 'Strategic insights', 'Action items', 'Risk assessment']
      },
      {
        id: 3,
        name: 'Customer Analytics Deep Dive',
        category: 'CRM',
        description: 'Analiză detaliată comportament clienți și segmentare',
        usage: 32,
        lastUpdated: '2024-02-28',
        elements: ['Customer segments', 'Behavior analysis', 'Lifetime value', 'Churn prediction']
      },
      {
        id: 4,
        name: 'Financial Performance Report',
        category: 'Financiar',
        description: 'Raport complet performanță financiară cu predicții',
        usage: 19,
        lastUpdated: '2024-02-20',
        elements: ['P&L analysis', 'Cash flow', 'Budget vs actual', 'Financial forecasting']
      }
    ],
    aiInsights: [
      {
        id: 1,
        insight: 'Vânzările pe mobile au crescut cu 34% în martie',
        confidence: 94,
        impact: 'Înalt',
        recommendation: 'Investiție în optimizarea experienței mobile',
        category: 'Trend Analysis'
      },
      {
        id: 2,
        insight: 'Clientii din segmentul Enterprise au cel mai mare potențial de upsell',
        confidence: 87,
        impact: 'Înalt',
        recommendation: 'Campanie targetată pentru servicii premium',
        category: 'Customer Intelligence'
      },
      {
        id: 3,
        insight: 'Produsele din categoria Accesorii se vând mai bine în pereche',
        confidence: 91,
        impact: 'Mediu',
        recommendation: 'Bundle offers și cross-selling automat',
        category: 'Product Intelligence'
      },
      {
        id: 4,
        insight: 'Echipa din Cluj are cea mai bună rată de conversie (18.5%)',
        confidence: 96,
        impact: 'Mediu',
        recommendation: 'Replicarea best practices în alte echipe',
        category: 'Performance Intelligence'
      }
    ]
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Activ': return 'success';
      case 'Pauză': return 'warning';
      case 'Oprit': return 'error';
      default: return 'default';
    }
  };

  const getFrequencyColor = (frequency) => {
    switch (frequency) {
      case 'Zilnic': return 'info';
      case 'Săptămânal': return 'success';
      case 'Bi-săptămânal': return 'warning';
      case 'Lunar': return 'default';
      default: return 'default';
    }
  };

  const getImpactColor = (impact) => {
    switch (impact) {
      case 'Înalt': return 'error';
      case 'Mediu': return 'warning';
      case 'Scăzut': return 'info';
      default: return 'default';
    }
  };

  const reportsColumns = [
    { 
      header: 'Raport', 
      key: 'name',
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{value}</div>
          <div className="text-sm text-gray-500">{row.description}</div>
        </div>
      )
    },
    { 
      header: 'Tip', 
      key: 'type',
      render: (value) => <Badge variant="default">{value}</Badge>
    },
    { 
      header: 'Frecvență', 
      key: 'frequency',
      render: (value) => <Badge variant={getFrequencyColor(value)}>{value}</Badge>
    },
    { 
      header: 'Status', 
      key: 'status',
      render: (value) => <Badge variant={getStatusColor(value)}>{value}</Badge>
    },
    { 
      header: 'Următoarea rulare', 
      key: 'nextRun',
      render: (value) => <span className="text-sm">{value}</span>
    },
    { 
      header: 'Destinatari', 
      key: 'recipients',
      render: (value) => <span className="text-sm font-medium">{value.length} persoane</span>
    },
    { 
      header: 'AI Insights', 
      key: 'insights',
      render: (value) => (
        <div className="flex items-center space-x-1">
          {value ? (
            <>
              <Zap className="w-3 h-3 text-yellow-500" />
              <span className="text-xs text-green-600">Da</span>
            </>
          ) : (
            <span className="text-xs text-gray-500">Nu</span>
          )}
        </div>
      )
    },
    {
      header: 'Acțiuni',
      key: 'actions',
      render: (_, row) => (
        <div className="flex space-x-1">
          <button
            onClick={() => {
              setSelectedReport(row);
              setShowReportModal(true);
            }}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            title="Vezi detalii"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-blue-500"
            title="Rulează acum"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  const recentColumns = [
    { 
      header: 'Raport Generat', 
      key: 'name',
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{value}</div>
          <div className="text-sm text-gray-500">{row.generatedAt}</div>
        </div>
      )
    },
    { 
      header: 'Tip', 
      key: 'type',
      render: (value) => <Badge variant="info">{value}</Badge>
    },
    { 
      header: 'Dimensiune', 
      key: 'size'
    },
    { 
      header: 'Engagement', 
      key: 'engagement',
      render: (_, row) => (
        <div className="text-sm">
          <div className="flex items-center space-x-2">
            <Eye className="w-3 h-3 text-gray-400" />
            <span>{row.views}</span>
            <Download className="w-3 h-3 text-gray-400 ml-2" />
            <span>{row.downloads}</span>
          </div>
        </div>
      )
    },
    { 
      header: 'Highlights', 
      key: 'highlights',
      render: (value) => (
        <div className="text-xs">
          {value.slice(0, 2).map((highlight, index) => (
            <div key={index} className="text-green-600">• {highlight}</div>
          ))}
          {value.length > 2 && (
            <div className="text-gray-500">+{value.length - 2} mai multe</div>
          )}
        </div>
      )
    },
    {
      header: 'Acțiuni',
      key: 'actions',
      render: (_, row) => (
        <div className="flex space-x-1">
          <button
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-blue-500"
            title="Download"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-green-500"
            title="Share"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Raportare Automată</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Generare automată de rapoarte cu insights AI și recomandări personalizate
          </p>
        </div>
        <div className="flex space-x-3">
          <button className="btn-secondary">
            <Download className="w-4 h-4 mr-2" />
            Export Toate
          </button>
          <button 
            className="btn-primary"
            onClick={() => setShowScheduleModal(true)}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Programează Raport
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card title="Total Rapoarte" subtitle="Generate vreodată" icon={FileText}>
          <div className="text-2xl font-bold text-primary-600">{reportData.overview.totalReports}</div>
          <div className="text-sm text-blue-600">+12 luna aceasta</div>
        </Card>
        <Card title="Programate" subtitle="Rapoarte automate" icon={Calendar}>
          <div className="text-2xl font-bold text-primary-600">{reportData.overview.scheduledReports}</div>
          <div className="text-sm text-green-600">{reportData.overview.automatedReports} cu AI insights</div>
        </Card>
        <Card title="Timp Economisit" subtitle="Prin automatizare" icon={Clock}>
          <div className="text-2xl font-bold text-primary-600">{reportData.overview.savedTime}</div>
          <div className="text-sm text-green-600">Echivalent 3 luni lucru</div>
        </Card>
        <Card title="AI Insights" subtitle="Generate automat" icon={Zap}>
          <div className="text-2xl font-bold text-primary-600">{reportData.aiInsights.length}</div>
          <div className="text-sm text-yellow-600">Insight-uri noi disponibile</div>
        </Card>
      </div>

      {/* Business Insights Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card title="Top Produs" subtitle="Cel mai vândut" icon={TrendingUp}>
          <div className="text-lg font-bold text-primary-600">{reportData.insights.topSellingProduct}</div>
        </Card>
        <Card title="Echipa Star" subtitle="Performanța cea mai bună" icon={Users}>
          <div className="text-lg font-bold text-primary-600">{reportData.insights.bestPerformingTeam}</div>
        </Card>
        <Card title="Creștere Revenue" subtitle="Față de perioada anterioară" icon={DollarSign}>
          <div className="text-lg font-bold text-green-600">{reportData.insights.revenueGrowth}</div>
        </Card>
        <Card title="AOV Mediu" subtitle="Average Order Value" icon={BarChart3}>
          <div className="text-lg font-bold text-primary-600">{reportData.insights.averageOrderValue}</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Scheduled Reports */}
        <div className="lg:col-span-2">
          <Card title="Rapoarte Programate" subtitle="Generare automată și distribuire">
            <div className="mb-4 flex items-center space-x-4">
              <select className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-sm">
                <option>Toate tipurile</option>
                <option>Vânzări</option>
                <option>Performanță</option>
                <option>CRM</option>
                <option>Financiar</option>
                <option>Inventar</option>
              </select>
              <select className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-sm">
                <option>Toate statusurile</option>
                <option>Activ</option>
                <option>Pauză</option>
                <option>Oprit</option>
              </select>
              <button className="btn-secondary btn-sm">
                <Filter className="w-3 h-3 mr-1" />
                Filtrează
              </button>
            </div>
            <Table columns={reportsColumns} data={reportData.scheduledReports} />
          </Card>
        </div>

        {/* AI Insights Sidebar */}
        <div className="space-y-6">
          <Card title="AI Insights Recente" icon={Zap}>
            <div className="space-y-4">
              {reportData.aiInsights.map((insight) => (
                <div key={insight.id} className="p-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 border-l-4 border-blue-500 rounded-r-lg">
                  <div className="flex items-center justify-between mb-2">
                    <Badge variant={getImpactColor(insight.impact)} className="text-xs">
                      {insight.impact}
                    </Badge>
                    <div className="flex items-center space-x-1">
                      <Activity className="w-3 h-3 text-blue-500" />
                      <span className="text-xs text-blue-600">{insight.confidence}%</span>
                    </div>
                  </div>
                  <div className="font-medium text-gray-900 dark:text-white text-sm mb-1">
                    {insight.insight}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    <strong>Recomandare:</strong> {insight.recommendation}
                  </div>
                  <Badge variant="default" className="text-xs">
                    {insight.category}
                  </Badge>
                </div>
              ))}
            </div>
            <button className="w-full mt-4 btn-primary btn-sm">
              Vezi Toate Insights
            </button>
          </Card>

          <Card title="Template-uri Populare" icon={FileText}>
            <div className="space-y-3">
              {reportData.templates.slice(0, 3).map((template) => (
                <div key={template.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-medium text-sm text-gray-900 dark:text-white">
                      {template.name}
                    </div>
                    <span className="text-xs text-gray-500">{template.usage} utilizări</span>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                    {template.description}
                  </div>
                  <Badge variant="info" className="text-xs">{template.category}</Badge>
                </div>
              ))}
            </div>
            <button className="w-full mt-4 btn-secondary btn-sm">
              Toate Template-urile
            </button>
          </Card>
        </div>
      </div>

      {/* Recent Reports */}
      <Card title="Rapoarte Generate Recent" subtitle="Ultimele rapoarte create și distribuite">
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Ultimele {reportData.recentReports.length} rapoarte generate
          </div>
          <div className="flex space-x-2">
            <select 
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white text-sm"
            >
              <option value="week">Ultima săptămână</option>
              <option value="month">Ultima lună</option>
              <option value="quarter">Ultimul trimestru</option>
            </select>
          </div>
        </div>
        <Table columns={recentColumns} data={reportData.recentReports} />
      </Card>

      {/* Schedule Report Modal */}
      <Modal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        title="Programează Raport Automat"
        size="large"
      >
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Numele Raportului
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                placeholder="Ex: Raport Vânzări Lunar"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Template de Bază
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white">
                <option>Sales Dashboard Standard</option>
                <option>Executive Summary</option>
                <option>Customer Analytics Deep Dive</option>
                <option>Financial Performance Report</option>
                <option>Template personalizat</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Frecvența
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white">
                <option>Zilnic</option>
                <option>Săptămânal</option>
                <option>Bi-săptămânal</option>
                <option>Lunar</option>
                <option>Trimestrial</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ora Trimiterii
              </label>
              <input
                type="time"
                defaultValue="09:00"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Format Export
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white">
                <option>PDF</option>
                <option>Excel</option>
                <option>PDF + Excel</option>
                <option>PowerPoint</option>
                <option>HTML Email</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Destinatari Email
            </label>
            <textarea
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
              placeholder="manager@company.ro, sales@company.ro, ceo@company.ro..."
            ></textarea>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Secțiuni Incluse
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                'Vânzări overview', 'Performance echipe', 'Analiza produse', 'Predicții AI',
                'Comparații YoY', 'KPI dashboard', 'Customer insights', 'Market trends',
                'Recomandări', 'Action items', 'Risk assessment', 'Budget analysis'
              ].map((section, index) => (
                <label key={index} className="flex items-center space-x-2 text-sm">
                  <input type="checkbox" className="rounded" defaultChecked={index < 6} />
                  <span>{section}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded" defaultChecked />
                <span className="text-sm">Include AI Insights</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded" defaultChecked />
                <span className="text-sm">Automated Recommendations</span>
              </label>
            </div>
            <div className="flex items-center space-x-4">
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded" />
                <span className="text-sm">Slack Notification</span>
              </label>
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="rounded" />
                <span className="text-sm">Dashboard Embed</span>
              </label>
            </div>
          </div>

          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Zap className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">AI Enhancement Preview</span>
            </div>
            <div className="text-sm text-blue-700 dark:text-blue-300">
              Raportul va include 4-6 AI insights, predicții pentru următoarele 30 de zile, și 3-5 recomandări
              acționabile generate automat pe baza datelor istorice și trendurilor de piață.
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button 
              className="btn-secondary"
              onClick={() => setShowScheduleModal(false)}
            >
              Anulează
            </button>
            <button className="btn-secondary">
              Salvează ca Draft
            </button>
            <button className="btn-primary">
              <Calendar className="w-4 h-4 mr-2" />
              Programează Raport
            </button>
          </div>
        </div>
      </Modal>

      {/* Report Details Modal */}
      <Modal
        isOpen={showReportModal}
        onClose={() => {
          setShowReportModal(false);
          setSelectedReport(null);
        }}
        title={`Detalii Raport: ${selectedReport?.name || ''}`}
        size="large"
      >
        {selectedReport && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Configurare Generală</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Tip:</span>
                    <Badge variant="default">{selectedReport.type}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Frecvență:</span>
                    <Badge variant={getFrequencyColor(selectedReport.frequency)}>{selectedReport.frequency}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Status:</span>
                    <Badge variant={getStatusColor(selectedReport.status)}>{selectedReport.status}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Format:</span>
                    <span>{selectedReport.format}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Automatizare:</span>
                    <span className="font-medium">{selectedReport.automation}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Schedule și Distribuire</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Următoarea rulare:</span>
                    <span className="font-medium">{selectedReport.nextRun}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Ultima trimitere:</span>
                    <span>{selectedReport.lastSent}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Destinatari:</span>
                    <span className="font-medium">{selectedReport.recipients.length} persoane</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">AI Insights:</span>
                    <div className="flex items-center space-x-1">
                      {selectedReport.insights ? (
                        <>
                          <Zap className="w-3 h-3 text-yellow-500" />
                          <span className="text-green-600 font-medium text-xs">Activat</span>
                        </>
                      ) : (
                        <span className="text-gray-500 text-xs">Dezactivat</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Descriere</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                {selectedReport.description}
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3">Lista Destinatari</h4>
              <div className="flex flex-wrap gap-2">
                {selectedReport.recipients.map((recipient, index) => (
                  <div key={index} className="flex items-center space-x-2 px-3 py-1 bg-blue-50 dark:bg-blue-900/20 rounded-full">
                    <Mail className="w-3 h-3 text-blue-500" />
                    <span className="text-sm text-blue-700 dark:text-blue-300">{recipient}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20 rounded-lg">
              <div className="flex items-center space-x-2 mb-2">
                <Activity className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-800 dark:text-green-200">Statistici Performanță</span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-green-600 font-semibold">96%</div>
                  <div className="text-green-700 dark:text-green-300">Rata livrare</div>
                </div>
                <div>
                  <div className="text-blue-600 font-semibold">78%</div>
                  <div className="text-blue-700 dark:text-blue-300">Open rate</div>
                </div>
                <div>
                  <div className="text-purple-600 font-semibold">34%</div>
                  <div className="text-purple-700 dark:text-purple-300">Engagement rate</div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button className="btn-secondary">Editează Configurare</button>
              <button className="btn-secondary bg-blue-50 text-blue-600 hover:bg-blue-100">
                <Send className="w-4 h-4 mr-2" />
                Rulează Acum
              </button>
              <button 
                className="btn-primary"
                onClick={() => {
                  setShowReportModal(false);
                  setSelectedReport(null);
                }}
              >
                Închide
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RaportareAutomata;
